<template>
  <div class="mt-8 space-y-4">
    <account-selection v-model="payment" />
    <account-picker
      v-if="payment === 'Specific Accounts'"
      :value="$v.ruleMembers.$model"
      @input="handleAccountPicker"
    />
    <div
      v-if="warnings('accounts').length > 0"
      class="bg-orange-100 p-4 border border-orange-200 rounded"
    >
      <ul class="text-orange-600 text-sm divide-y divide-orange-200">
        <li
          v-for="(warning, idx) in warnings('accounts')"
          :key="idx"
          class="py-2"
          :class="{ 'text-red-700': !warning.allow }"
        >
        <div class="flex space-x-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <!-- eslint-disable-next-line max-len -->
            <path v-if="!warning.allow" fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            <!-- eslint-disable-next-line max-len -->
            <path v-else fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          <h5 class="font-bold">{{ warning.title }}</h5>
        </div>
        <p>{{warning.message}}</p>
        <p v-if="!warning.allow" class="text-xs font-bold">Reselection Required</p>
        </li>
      </ul>
    </div>
    <in-this-way
      v-if="waySelections.length > 0"
      v-model="$v.way.$model"
      :selections="waySelections"
    >
      <template v-slot="{ selected }">
        <div v-if="selected">
          <in-this-way-extra
            v-for="(selection, idx) in wayExtraSelections"
            :key="idx"
            :extra="selection"
            v-model="$v.extra.$model"
            :members="ruleMembers"
            :accounts="ruleAccounts"
            @accounts="handleAccounts"
          >
            <template
              #error
              v-if="!$v.extra.total && $v.extra.$model === selection.label"
            >
              Total must add to 100
            </template>
          </in-this-way-extra>
        </div>
      </template>
    </in-this-way>
    <until
      v-show="untilSelections.length > 0"
      v-model="$v.until.$model"
      :selections="untilSelections"
    />
    <div class="flex justify-end space-x-2">
      <m-button variant="secondary" @click="handleClose">Cancel</m-button>
      <m-button
        data-test="add-rule-save"
        @click="handleSave"
        :disabled="$v.$invalid || hasAccountsError"
        :class="{ 'cursor-not-allowed': $v.$invalid || hasAccountsError }"
        >Save</m-button
      >
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { required, requiredIf, minLength } from 'vuelidate/lib/validators';

import { pensionAddInstructionMixin } from '@/app/mixins/toolkit/pension-instructions/add-instruction';

import AccountSelection from './accounts/account-selection.vue';
import InThisWay from './in-this-way/in-this-way.vue';
import InThisWayExtra from './in-this-way/in-this-way-extra.vue';
import Until from './until.vue';
import AccountPicker from './accounts/account-picker.vue';

const accountTypeMap = {
  'All Accumulation Accounts': 'accumulation',
  'All Pension Accounts': 'pension',
};

function accountsWarnings(warnings, accounts) {
  const result = [];
  _.keys(warnings).forEach((key) => {
    const hasAccount = accounts.some(account => key === account.label);
    if (hasAccount) {
      result.push({
        title: key,
        ...warnings[key],
      });
    }
  });
  return result;
}

export default {
  name: 'payment-instruction-add',
  mixins: [pensionAddInstructionMixin],
  components: {
    AccountSelection,
    InThisWay,
    InThisWayExtra,
    Until,
    AccountPicker,
  },
  props: {
    editRule: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      ruleMembers: {},
      ruleAccounts: [],
      payment: '',
      way: '',
      extra: '',
      until: {
        selection: '',
        inputValue: 0,
      },
    };
  },
  computed: {
    hasAccountsError() {
      return this.warnings('accounts').some(warning => !warning.allow);
    },
    warnings() {
      const fnMap = {
        accountsWarnings,
      };

      return (type) => {
        if (this.rulesOutcome.length === 1) {
          if (this.rulesOutcome[0][type].warnings) {
            return fnMap[`${type}Warnings`](this.rulesOutcome[0][type].warnings, this.ruleAccounts);
          }
          return [];
        }
        return [];
      };
    },
    waySelections() {
      const rules = this.rules.filter(
        rule => rule.accounts.accounts === this.numAccountsType,
      );
      const way = _.groupBy(rules, 'way.label');
      return _.keys(way).map(key => ({
        rules: way[key],
        label: key,
      }));
    },
    wayExtraSelections() {
      const rules = this.rules
        .filter(rule => rule.accounts.accounts === this.numAccountsType)
        .filter(rule => rule.way.label === this.way)
        .filter(rule => rule.extra !== undefined);
      const wayExtra = _.groupBy(rules, 'extra.label');
      if (_.keys(wayExtra).length > 0) {
        return _.keys(wayExtra).map(key => ({
          rules: wayExtra[key],
          label: key,
          ...wayExtra[key][0].extra,
        }));
      }
      return [];
    },
    untilSelections() {
      if (this.way !== '') {
        const rules = this.rules
          .filter(rule => rule.accounts.accounts === this.numAccountsType)
          .filter(rule => rule.way.label === this.way);
        const until = _.groupBy(rules, 'until.label');
        return _.keys(until).map(key => ({
          rules: until[key],
          label: key,
        }));
      }
      return [];
    },
  },
  provide() {
    return {
      members: this.ruleMembers,
    };
  },
  methods: {
    resetSelections() {
      this.ruleMembers = {};
      this.ruleAccounts = [];
      this.payment = '';
      this.way = '';
      this.extra = '';
      this.until = {
        selection: '',
        inputValue: 0,
      };
    },
    handleAccountPicker(members) {
      if (!_.isEqual(this.ruleMembers, members)) {
        this.way = '';
        this.extra = '';
        this.until = {
          selection: '',
          inputValue: 0,
        };
      }

      this.ruleMembers = members;
      this.updateRuleAccounts(members);
    },
    updateRuleAccounts(members) {
      const ruleAccounts = [];
      _.forEach(members, (member) => {
        ruleAccounts.push(
          ..._.values(member.accounts).map(account => ({
            member,
            ...account,
          })),
        );
      });

      this.ruleAccounts = ruleAccounts;
    },
    handleAccounts(e) {
      this.ruleAccounts = e;
    },
    handleSave() {
      const rule = _.cloneDeep(this.rulesOutcome[0]);
      rule.members = this.ruleMembers;
      rule.accounts.payment = this.payment;
      rule.accounts.ruleAccounts = this.ruleAccounts;
      if (this.until.inputValue > 0) {
        rule.until.inputValue = this.until.inputValue;
      }
      this.resetSelections();
      this.$emit('rule', {
        type: _.keys(this.editRule).length > 0 ? 'edit' : 'add',
        idx: _.keys(this.editRule).length > 0 ? this.editRule.idx : undefined,
        rule,
      });
    },
    handleClose() {
      this.resetSelections();
      this.$emit('close');
    },
  },
  watch: {
    payment(val, prev) {
      if (_.keys(this.editRule).length > 0 && prev === '') return;

      this.resetSelections();
      this.payment = val;
      if (val === 'Specific Accounts' || val === '') {
        // Accounts will be chosen in account picker
        return;
      }
      // Get all accounts by type
      const type = accountTypeMap[val];
      const members = _.mapValues(this.getItem.members, member => ({
        ...member,
        accounts: _.pickBy(member.accounts, account => account.type === type),
      }));

      this.ruleMembers = members;
      this.updateRuleAccounts(members);
    },
    way(_val, prev) {
      if (_.keys(this.editRule).length > 0 && prev === '') return;
      this.extra = '';
      this.until = {
        selection: '',
        inputValue: 0,
      };
    },
  },
  mounted() {
    if (_.keys(this.editRule).length > 0) {
      this.payment = this.editRule.rule.accounts.payment;
      this.ruleAccounts = [...this.editRule.rule.accounts.ruleAccounts];
      this.ruleMembers = this.editRule.rule.members;
      this.way = this.editRule.rule.way.label;
      if (this.editRule.rule.extra) {
        this.extra = this.editRule.rule.extra.label;
      }
      this.until.selection = this.editRule.rule.until.label;
      if (this.editRule.rule.until.inputValue) {
        this.until.inputValue = this.editRule.rule.until.inputValue;
      }
    }
  },
  validations() {
    if (this.wayExtraSelections.length > 0) {
      return {
        ruleMembers: {
          required,
          minLength: minLength(1),
        },
        way: {
          required,
        },
        extra: {
          required: requiredIf(val => val.wayExtraSelections.length > 0),
          total: (value) => {
            const reqValues = [
              'divided between the two accounts as follows',
              'divided between pension accounts as follows',
            ];

            if (!reqValues.includes(value)) return true;

            const accountTotal = this.ruleAccounts.reduce(
              (prev, curr) => prev + +curr.value,
              0,
            );
            return accountTotal >= 99.9 && accountTotal <= 100.1;
          },
        },
        until: {
          selection: {
            required,
          },
        },
      };
    }
    return {
      ruleMembers: {
        required,
        minLength: minLength(1),
      },
      way: {
        required,
      },
      extra: {
        required: requiredIf(val => val.wayExtraSelections.length > 0),
      },
      until: {
        selection: {
          required,
        },
      },
    };
  },
};
</script>
